import { Injectable } from '@angular/core';
import { User } from 'avn/modules/shared/interfaces/user';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

const CLEANUP_DELAY_MS = 100;

@Injectable({
  providedIn: 'root'
})
export class AuthAppService {
  private static readonly key_frontend = 'WORKCOVER_JWT_KEY';
  private static readonly key_backend = 'WORKCOVER_JWT_KEY_BACKEND';

  private readonly workcoverLoggedUserSubject = new BehaviorSubject<User>(this.getSavedUser() as User);
  public workcoverLoggedUser = this.workcoverLoggedUserSubject.asObservable().pipe(distinctUntilChanged());

  private readonly wellBeingDetailSubject = new BehaviorSubject<any>(this.getWellbeingDetails());
  public wellBeingDetail = this.wellBeingDetailSubject.asObservable().pipe(distinctUntilChanged());

  private readonly isAuthenticatedSubject = new ReplaySubject<boolean>(1);
  public isAuthenticated = this.isAuthenticatedSubject.asObservable();

  public clientIdType: 'frontend' | 'backend' = 'frontend';

  public get isLoggedIn(): boolean {
    try {
      return !!localStorage.getItem(AuthAppService.key_frontend);
    } catch {
      return false;
    }
  }

  public get isBackendLoggedIn(): boolean {
    try {
      return !!localStorage.getItem(AuthAppService.key_backend);
    } catch {
      return false;
    }
  }

  public clientType(): string | null {
    try {
      return localStorage.getItem('ClientType');
    } catch {
      return 'frontend';
    }
  }

  public getSavedUser(): User | null {
    try {
      const cuser = localStorage.getItem('workcoverLoggedUser');
      if (cuser) {
        const workcoverLoggedUser = JSON.parse(cuser);
        return workcoverLoggedUser;
      }
      return null;
    } catch {
      return null;
    }
  }

  public setAuth(token: string): void {
    localStorage.setItem(AuthAppService.key_frontend, token);
    localStorage.setItem('ClientType', 'frontend');
    localStorage.setItem('workCoverLastLogin', new Date().toString());
    this.isAuthenticatedSubject.next(true);
  }

  public setBackendAuth(token: string): void {
    localStorage.setItem(AuthAppService.key_backend, token);
    localStorage.setItem('ClientType', 'backend');
    localStorage.setItem('workCoverLastLogin', new Date().toString());
  }

  public getToken(): string | null {
    try {
      return localStorage.getItem(AuthAppService.key_frontend);
    } catch {
      return '';
    }
  }

  public getBackendToken(): string | null {
    try {
      return localStorage.getItem(AuthAppService.key_backend);
    } catch {
      return '';
    }
  }

  public setworkcoverLoggedUser(user: User): void {
    localStorage.setItem('workcoverLoggedUser', JSON.stringify(user));
    localStorage.setItem('workCoverLastLogin', new Date().toString());
    this.workcoverLoggedUserSubject.next(user);
  }

  public setWellbeingDetails(wellbeing: any): void {
    localStorage.setItem('wellbeing', JSON.stringify(wellbeing));
    this.wellBeingDetailSubject.next(wellbeing);
  }

  public getWellbeingDetails(): Record<string, unknown> | string {
    try {
      return JSON.parse(localStorage.getItem('wellbeing') || '{}');
    } catch {
      return '{}';
    }
  }

  public getworkcoverLoggedUser(): User {
    return this.workcoverLoggedUserSubject.value;
  }

  public clearUserData(): void {
    localStorage.removeItem('workcoverLoggedUser');
    localStorage.removeItem('workCoverLastLogin');
    localStorage.removeItem('wellbeing');
    localStorage.removeItem('ClientType');
    localStorage.removeItem(AuthAppService.key_frontend);
    localStorage.removeItem(AuthAppService.key_backend);
  }

  public purgeAuth(): void {
    // Remove token  from localstorage
    this.clearUserData();
    // Set current user to an empty object
    this.workcoverLoggedUserSubject.next(new User());
    // Set auth status to false
    this.isAuthenticatedSubject.next(false);
    setTimeout(() => {
      localStorage.removeItem('workCoverLastLogin');
    }, CLEANUP_DELAY_MS);
    return;
  }
}
